import { CheckIcon, WarningOctagonIcon, XIcon } from "@phosphor-icons/react";
import { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";

interface ConfirmOptions {
  title: string;
  body?: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  danger?: boolean;
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;
const Ctx = createContext<ConfirmFn | null>(null);

/** Native <dialog> confirm: focus trap, Esc to cancel, focus returns to the trigger. */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  const [opts, setOpts] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((v: boolean) => void) | null>(null);
  const returnFocus = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const bodyId = useId();

  const confirm = useCallback<ConfirmFn>((o) => {
    returnFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setOpts(o);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  useEffect(() => {
    const d = ref.current;
    if (opts && d && !d.open) d.showModal();
  }, [opts]);

  const finish = useCallback((v: boolean) => {
    const d = ref.current;
    if (d?.open) d.close();
    resolver.current?.(v);
    resolver.current = null;
    setOpts(null);
    returnFocus.current?.focus();
  }, []);

  const value = useMemo(() => confirm, [confirm]);
  return (
    <Ctx.Provider value={value}>
      {children}
      <dialog
        ref={ref}
        className="dialog"
        aria-labelledby={titleId}
        aria-describedby={opts?.body ? bodyId : undefined}
        onCancel={(e) => {
          e.preventDefault();
          finish(false);
        }}
      >
        {opts ? (
          <>
            <div className="dialog__body">
              <h2 id={titleId} className="dialog__title">
                {opts.title}
              </h2>
              {opts.body ? (
                <div id={bodyId} className="muted">
                  {opts.body}
                </div>
              ) : null}
            </div>
            <div className="dialog__actions">
              <button type="button" className="btn" onClick={() => finish(false)} autoFocus><XIcon size={16} aria-hidden="true" />
                {opts.cancelLabel ?? "Cancel"}
              </button>
              <button type="button" className={`btn ${opts.danger ? "btn--danger" : "btn--primary"}`} onClick={() => finish(true)}>{opts.danger ? <WarningOctagonIcon size={16} aria-hidden="true" /> : <CheckIcon size={16} aria-hidden="true" />}
                {opts.confirmLabel}
              </button>
            </div>
          </>
        ) : null}
      </dialog>
    </Ctx.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const v = useContext(Ctx);
  if (!v) throw new Error("useConfirm outside ConfirmProvider");
  return v;
}
